import { useQuery } from "@tanstack/react-query";
import type { CreateKubernetesRequest, UpdateKubernetesRequest } from "@microtica/ms-kube-sdk";

import { getKubeService } from "../../backend";

/**
 * The project's Kubernetes clusters. A connected ("personal") cluster is the project's own, reached
 * with the address and token it was connected with; those can be changed here. Managed clusters
 * come from a cluster component and shared ones from Microtica, so neither takes new credentials.
 * Cluster ids start with their environment's id: `${envId}-…`.
 */

export type ClusterType = "managed" | "personal" | "shared";

/**
 * How the cluster pulls images from ECR registries. `auto`: a pull secret for access-key
 * registries, the nodes' IAM role for registries on the connected AWS account. `enabled`: always a
 * pull secret. `disabled`: never; the nodes pull with their own role. Other registries always get one.
 */
export type EcrPullSecret = "auto" | "enabled" | "disabled";

export const ECR_PULL_OPTIONS: { value: EcrPullSecret; label: string; hint: string }[] = [
    { value: "auto", label: "Automatic", hint: "A pull secret for registries with access keys, the node IAM role for registries on a connected AWS account." },
    { value: "enabled", label: "Pull secret", hint: "Always a pull secret. Only works with registries that use access keys." },
    { value: "disabled", label: "Node IAM role", hint: "No pull secret. The nodes must be able to pull from the registry's AWS account." },
];

/** Clusters saved before the setting existed are `auto` */
export const ecrPullSecretOf = (cluster: { ecrPullSecret?: string }): EcrPullSecret =>
    cluster.ecrPullSecret === "enabled" || cluster.ecrPullSecret === "disabled" ? cluster.ecrPullSecret : "auto";

// `ecrPullSecret` isn't in @microtica/ms-kube-sdk yet
export type ClusterUpdateRequest = UpdateKubernetesRequest & { ecrPullSecret?: EcrPullSecret };
export type ClusterCreateRequest = CreateKubernetesRequest & { ecrPullSecret?: EcrPullSecret };

export interface Cluster {
    id: string;
    name: string;
    endpoint: string;
    type: ClusterType;
    /** Set when Microtica deploys into one namespace of the cluster rather than all of it */
    namespace?: string;
    envId?: string;
    envName?: string;
    /** The cluster component that created it: its id is `${envId}-${component}` */
    component?: string;
    /** The environment that created it, when that's been deleted since */
    goneEnv?: string;
    /** Its environment is still listed, but undeployed, so the cluster component is gone with it */
    envRemoved?: boolean;
    /** How it pulls from ECR registries */
    ecrPullSecret?: EcrPullSecret;
}

type RawCluster = { id: string; name: string; endpoint: string; type: string; associatedNamespace?: string; ecrPullSecret?: string };

/** Each cluster with its environment, in the environments' order */
export function withEnvironments(list: RawCluster[], envs: { id: string; name: string; status?: string }[]): Cluster[] {
    const envOf = (id: string) => envs
        .filter(e => id.startsWith(`${e.id}-`))
        .sort((a, b) => b.id.length - a.id.length)[0];
    const order = (c: Cluster) => (c.envId ? envs.findIndex(e => e.id === c.envId) : envs.length);
    return list
        .map(c => {
            const env = envOf(c.id);
            // A cluster component's deploy registers its cluster as "personal", named by its id
            const fromComponent = !c.associatedNamespace && (c.type === "managed" || c.name === c.id);
            // Environment ids end in a 10-character suffix: "development-UxvFTLVAIm"
            const gone = fromComponent && !env ? /^(.+)-[A-Za-z0-9]{10}-(.+)$/.exec(c.id) : null;
            const component = fromComponent && env ? c.id.slice(env.id.length + 1) : gone?.[2];
            // Undeploying the environment deletes the cluster but leaves its record behind
            const envRemoved = fromComponent && !!env && /^(NOT_DEPLOYED|DELETE_COMPLETE)$/.test(env.status || "");
            return { id: c.id, name: c.name, endpoint: c.endpoint, type: c.type as ClusterType, namespace: c.associatedNamespace || undefined, envId: env?.id, envName: env?.name, component, goneEnv: gone?.[1], envRemoved, ecrPullSecret: ecrPullSecretOf(c) };
        })
        .sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name));
}

/**
 * One physical cluster. Microtica keeps a record per way in: the cluster itself, a record per
 * namespace scoped from it (with its own `client-account` token, tied to the cluster only by the
 * address), and sometimes extra records with a token of their own, such as a read-only one.
 */
export interface ClusterGroup {
    endpoint: string;
    /** The record the namespaces were scoped from; the component's cluster when there is one */
    root?: Cluster;
    title: string;
    namespaces: Cluster[];
    /** Other whole-cluster records, each with its own token */
    others: Cluster[];
    /** No record of it is used by an environment */
    unused: boolean;
}

/** The records grouped by the cluster they reach, in the environments' order; unused ones last */
export function groupClusters(list: Cluster[]): ClusterGroup[] {
    const byEndpoint = new Map<string, Cluster[]>();
    list.forEach(c => byEndpoint.set(c.endpoint, [...(byEndpoint.get(c.endpoint) || []), c]));
    const groups = [...byEndpoint.entries()].map(([endpoint, records]) => {
        const whole = records.filter(c => !c.namespace);
        const root = whole.find(c => c.component) || whole.find(c => c.type === "managed") || whole.find(c => c.envId) || whole[0];
        return {
            endpoint,
            root,
            title: root ? root.component || root.name : hostOf(endpoint),
            namespaces: records.filter(c => c.namespace),
            others: whole.filter(c => c !== root),
            unused: !records.some(c => c.envId),
        };
    });
    // `list` is already in the environments' order, so a group's first record places it
    const first = (g: ClusterGroup) => list.findIndex(c => c.endpoint === g.endpoint);
    return groups.sort((a, b) => Number(a.unused) - Number(b.unused) || first(a) - first(b));
}

/** "https://F871….gr7.eu-central-1.eks.amazonaws.com" reads as its host */
export const hostOf = (endpoint: string) => endpoint.replace(/^https?:\/\//, "").replace(/[:/].*$/, "");

/** A cluster's access is the project's to change unless a component's deploy keeps it */
export const editable = (cluster: Cluster) => cluster.type === "personal" && !cluster.component;

export interface QuotaInput { cpuRequests: string; cpuLimits: string; memoryRequests: string; memoryLimits: string }
export const NO_QUOTAS: QuotaInput = { cpuRequests: "", cpuLimits: "", memoryRequests: "", memoryLimits: "" };

/**
 * The namespace's resource quota from the form, in mCPU and MiB. The API needs all four and can't
 * say what's set now, so all blank keeps the current quota.
 */
export function quotaChange(input: QuotaInput): { quotas?: UpdateKubernetesRequest["resourceQuotas"]; error?: string } {
    const values = Object.values(input).map(v => v.trim());
    if (values.every(v => !v)) return {};
    if (values.some(v => !v)) return { error: "Fill in all four, or leave them all blank to keep the current quota." };
    if (values.some(v => !/^\d+$/.test(v) || Number(v) <= 0)) return { error: "Use whole numbers above zero." };
    const [cpuRequests, cpuLimits, memoryRequests, memoryLimits] = [input.cpuRequests, input.cpuLimits, input.memoryRequests, input.memoryLimits].map(Number);
    if (cpuRequests > cpuLimits || memoryRequests > memoryLimits) return { error: "A request can't be more than its limit." };
    return { quotas: { requests: { cpu: cpuRequests, memory: memoryRequests }, limits: { cpu: cpuLimits, memory: memoryLimits } } };
}

export const validEndpoint = (endpoint: string) => /^https?:\/\/\S+$/.test(endpoint.trim());

/** What to send: the token only when a new one is given, the quota only when all of it is */
export function clusterUpdate(form: { name: string; endpoint: string; token: string }, quotas?: UpdateKubernetesRequest["resourceQuotas"]): UpdateKubernetesRequest {
    return {
        name: form.name.trim(),
        endpoint: form.endpoint.trim(),
        ...(form.token.trim() ? { apiToken: form.token.trim() } : {}),
        ...(quotas ? { resourceQuotas: quotas } : {}),
    };
}

export const clusterKeys = {
    list: (projectId: string) => ["v2", "clusters", projectId] as const,
    status: (projectId: string, id: string) => ["v2", "clusters", projectId, id, "status"] as const,
};

export function useClusters(projectId: string) {
    return useQuery({
        queryKey: clusterKeys.list(projectId),
        queryFn: async () => (await getKubeService().listKubernetes(projectId)).data.kuberneteses as RawCluster[],
    });
}

/** Whether Microtica can reach the cluster now, and what runs on it */
export function useClusterStatus(projectId: string, id: string) {
    return useQuery({
        queryKey: clusterKeys.status(projectId, id),
        queryFn: async () => {
            const { data } = await getKubeService().getKubernetesStatus(id, projectId);
            return { nodes: data.nodesStatus?.totalNodes ?? 0, pods: data.podStatus?.runningPods ?? 0 };
        },
        retry: false,
        staleTime: 60 * 1000,
    });
}

/** "2 nodes · 14 pods running" */
export function statusLine(status: { nodes: number; pods: number }) {
    const nodes = status.nodes ? `${status.nodes} node${status.nodes === 1 ? "" : "s"} · ` : "";
    return `${nodes}${status.pods} pod${status.pods === 1 ? "" : "s"} running`;
}
